import "./abProducts.style.scss";
import { Mount, SensorType, ProductSubCategoryEnum, ProductTypeEnum, ProductUsageEnum } from "@shared/enums";
import { ProductListingController } from "./abProducts.controller";
import { parseProductFilterOpen, parseProductListingConfig, parseProductListingInterstitialRow, parseProductSliderLayoutConfig, ProductListingConfigurationError, ProductSliderLayoutConfig } from "./abProducts.config";
import { PRODUCT_FILTER_TAGS, ProductFilterTag, ProductListingConfig, ProductListingState } from "./abProducts.types";
import { createProductCardViewModels } from "./variants/productListingItem";
import { ProductGridRenderer } from "./renderers/productGrid.renderer";
import { ProductSliderRenderer } from "./renderers/productSlider.renderer";
import {
  getBrandOptions, getProductTypeOptions, getLensMountOptions, getSensorFormatOptions, getTargetOptions, getSubCategoryOptions, getUsageOptions,
} from "./filtering/productFilterEngine";
import { AbFilterBrandElement, defineAbFilterBrandElement } from "./filtering/abFilterBrand.element";
import { AbFilterProductTypeElement, defineAbFilterProductTypeElement } from "./filtering/abFilterProductType.element";
import { AbFilterSensorFormatElement, defineAbFilterSensorFormatElement } from "./filtering/abFilterSensorFormat.element";
import { AbFilterLensCompatibilityElement, defineAbFilterLensCompatibilityElement } from "./filtering/abFilterLensCompatibility.element";
import { AbFilterTargetElement, defineAbFilterTargetElement } from "./filtering/abFilterTarget.element";
import { AbFilterSubCategoryElement, defineAbFilterSubCategoryElement } from "./filtering/abFilterSubCategory.element";
import { AbFilterUsageElement, defineAbFilterUsageElement } from "./filtering/abFilterUsage.element";
import { readCameraFilterConfig } from "./filtering/cameraFilterConfig";
import { getProductListingCountLabel } from "./productListingCountLabel";
import { AbModal } from "@/scripts/modules/ab-modal/ab-modal.class";
import { createSvg } from "@/scripts/utils/dom.utils";

const TAG_NAME = "ab-products";
const SIDEBAR_MIN_WIDTH = 944;
const FILTER_CHIP_LABELS: Record<ProductFilterTag, string> = {
  "ab-filter-brand": "Produttore",
  "ab-filter-product-type": "Tipologia prodotto",
  "ab-filter-sensor-format": "Formato sensore",
  "ab-filter-lens-compatibility": "Compatibilità",
  "ab-filter-target": "Livello",
  "ab-filter-sub-category": "Categoria",
  "ab-filter-usage": "Utilizzo",
};
const FILTER_SELECTION_KEYS = {
  "ab-filter-brand": "brands",
  "ab-filter-product-type": "productTypes",
  "ab-filter-sensor-format": "sensorFormats",
  "ab-filter-lens-compatibility": "compatibleLensMounts",
  "ab-filter-target": "targets",
  "ab-filter-sub-category": "subCategories",
  "ab-filter-usage": "usages",
} as const satisfies Record<ProductFilterTag, keyof ProductListingState["selections"]>;
type ProductRenderer = ProductGridRenderer | ProductSliderRenderer;

export class AbProductsElement extends HTMLElement {
  #sectionLinks: Readonly<Record<string, string>> = Object.freeze({});

  get sectionLinks(): Readonly<Record<string, string>> { return this.#sectionLinks; }
  set sectionLinks(value: Readonly<Record<string, string>>) {
    if (!value || Object.values(value).some((link) => typeof link !== 'string' || !/^#[^\s#]+$/.test(link))) {
      throw new TypeError('sectionLinks must map product ids to local anchors');
    }
    this.#sectionLinks = Object.freeze({ ...value });
    if (this.#controller) this.#renderState(this.#controller.state);
  }

  #controller?: ProductListingController;
  #renderer?: ProductRenderer;
  #unsubscribe?: () => void;
  #filterElements?: HTMLElement[];
  #filterPanel?: HTMLElement;
  #results?: HTMLElement;
  #filterTrigger?: HTMLButtonElement;
  #filterToolbar?: HTMLElement;
  #filterButtons = new Map<HTMLElement, HTMLButtonElement>();
  #modal?: AbModal;
  #layoutObserver?: ResizeObserver;
  #filtersReady = false;
  #resultsHeader?: HTMLElement;
  #summary?: HTMLElement;
  #reset?: HTMLButtonElement;
  #empty?: HTMLElement;
  #configurationError?: HTMLElement;

  static get observedAttributes(): string[] {
    return ["data-include", "data-source", "data-collection-type", "data-collection-id", "data-product-ids", "data-display", "data-constraints", "data-variant-scope", "data-filters", "data-interstitial-id", "data-interstitial-after-row", "data-layout", "data-card-breakpoints", "data-view"];
  }

  connectedCallback() {
    if (!this.#filterElements) {
      this.#filterElements = Array.from(this.children).filter((child): child is HTMLElement =>
        (PRODUCT_FILTER_TAGS as readonly string[]).includes(child.tagName.toLowerCase()));
      this.#getFilter("ab-filter-brand")?.addEventListener("brand-filter-change", this.#onBrandChange);
      this.#getFilter("ab-filter-product-type")?.addEventListener("product-type-filter-change", this.#onProductTypeChange);
      this.#getFilter("ab-filter-sensor-format")?.addEventListener("sensor-format-filter-change", this.#onSensorFormatChange);
      this.#getFilter("ab-filter-lens-compatibility")?.addEventListener("lens-compatibility-change", this.#onLensCompatibilityChange);
      this.#getFilter("ab-filter-target")?.addEventListener("target-filter-change", this.#onTargetChange);
      this.#getFilter("ab-filter-sub-category")?.addEventListener("sub-category-filter-change", this.#onSubCategoryChange);
      this.#getFilter("ab-filter-usage")?.addEventListener("usage-filter-change", this.#onUsageChange);
    }
    if (this.#layoutObserver) {
      this.#layoutObserver.observe(this);
      this.#syncLayout(this.clientWidth);
    }
    void this.#update();
  }
  disconnectedCallback() {
    this.#modal?.hide();
    this.#layoutObserver?.disconnect();
    this.#unsubscribe?.(); this.#unsubscribe = undefined; this.#destroyRenderer();
  }
  attributeChangedCallback() { if (this.isConnected) void this.#update(); }

  #onBrandChange = (event: Event) => this.#controller?.setBrands((event as CustomEvent<readonly string[]>).detail);
  #onProductTypeChange = (event: Event) => this.#controller?.setProductTypes((event as CustomEvent<readonly ProductTypeEnum[]>).detail);
  #onSensorFormatChange = (event: Event) => this.#controller?.setSensorFormats((event as CustomEvent<readonly SensorType[]>).detail);
  #onLensCompatibilityChange = (event: Event) => this.#controller?.setCompatibleLensMounts((event as CustomEvent<readonly Mount[]>).detail);
  #onTargetChange = (event: Event) => this.#controller?.setTargets((event as CustomEvent<readonly number[]>).detail);
  #onSubCategoryChange = (event: Event) => this.#controller?.setSubCategories((event as CustomEvent<readonly ProductSubCategoryEnum[]>).detail);
  #onUsageChange = (event: Event) => this.#controller?.setUsages((event as CustomEvent<readonly ProductUsageEnum[]>).detail);

  async #update() {
    let config: ProductListingConfig;
    let sliderLayout: ProductSliderLayoutConfig | undefined;
    let interstitialTemplate: HTMLTemplateElement | null;
    let interstitialAfterRow: number | null;
    try {
      config = parseProductListingConfig(this, this.#filterElements!.map((element) => element.tagName.toLowerCase() as ProductFilterTag));
      if (config.display === "slider") sliderLayout = parseProductSliderLayoutConfig(this);
      this.#filterElements!.forEach(parseProductFilterOpen);
      const templates = Array.from(this.children).filter((child): child is HTMLTemplateElement =>
        child instanceof HTMLTemplateElement);
      interstitialAfterRow = parseProductListingInterstitialRow(
        templates.map((template) => template.getAttribute("data-interstitial-after-row")), config.display,
      );
      interstitialTemplate = templates[0] ?? null;
      const compatibility = this.#getFilter("ab-filter-lens-compatibility");
      if (compatibility) readCameraFilterConfig(compatibility);
    } catch (error) { this.#showConfigurationError(error); return; }

    this.#configurationError?.remove();
    this.#configurationError = undefined;
    this.#filterElements?.forEach((element) => { element.hidden = false; });
    const sourceChanged = !!this.#controller && this.#controller.config.source !== config.source;
    if (sourceChanged) (this.#getFilter("ab-filter-lens-compatibility") as AbFilterLensCompatibilityElement | undefined)?.resetSelection();
    this.#ensureFilterPanel(config);
    this.#ensureRenderer(config);
    if (config.display === "slider") {
      (this.#renderer as ProductSliderRenderer).setLayoutConfig(sliderLayout!);
    }
    if (config.display === "grid") {
      (this.#renderer as ProductGridRenderer).setInterstitial(interstitialTemplate, interstitialAfterRow);
    }
    if (!this.#controller) {
      this.#controller = new ProductListingController(config);
      this.#unsubscribe = this.#controller.subscribe((state) => this.#renderState(state));
      await this.#controller.load();
      return;
    }
    if (!this.#unsubscribe) this.#unsubscribe = this.#controller.subscribe((state) => this.#renderState(state));
    await this.#controller.updateConfig(config);
  }

  #getFilter(tag: ProductFilterTag): HTMLElement | undefined {
    return this.#filterElements?.find((element) => element.tagName.toLowerCase() === tag);
  }

  #ensureRenderer(config: ProductListingConfig) {
    const needsGrid = config.display === "grid";
    if (this.#renderer && ((needsGrid && this.#renderer instanceof ProductGridRenderer) || (!needsGrid && this.#renderer instanceof ProductSliderRenderer))) return;
    this.#destroyRenderer();
    this.#renderer = needsGrid
      ? new ProductGridRenderer(this.#results ?? this, { className: "ab-products" })
      : new ProductSliderRenderer(this.#results ?? this, this, "ab-products");
  }

  #ensureFilterPanel(config: ProductListingConfig) {
    if (!config.filters.length || this.#filterPanel) return;
    this.#filterPanel = document.createElement("aside");
    this.#filterPanel.className = "ab-products__filters";
    this.#filterPanel.setAttribute("aria-label", "Filtri prodotti");
    this.#results = document.createElement("div");
    this.#results.className = "ab-products__results";
    this.#filterTrigger = document.createElement("button");
    this.#filterTrigger.type = "button";
    this.#filterTrigger.className = "ab-products__filter-trigger ab-btn ab-btn--outline";
    this.#filterTrigger.append(createSvg("icon-filter"), document.createTextNode("Filtri"));
    this.#filterTrigger.setAttribute("aria-haspopup", "dialog");
    this.#filterTrigger.addEventListener("click", () => this.#openFilters());
    this.#filterToolbar = document.createElement("div");
    this.#filterToolbar.className = "ab-products__filter-toolbar";
    this.#filterToolbar.setAttribute("aria-label", "Filtri disponibili");
    this.#filterToolbar.append(this.#filterTrigger);
    this.#filterElements!.forEach((filter) => {
      const tag = filter.tagName.toLowerCase() as ProductFilterTag;
      const button = document.createElement("button");
      button.type = "button";
      button.className = "ab-products__filter-chip ab-btn ab-btn--outline";
      button.textContent = filter.getAttribute("label") || FILTER_CHIP_LABELS[tag];
      button.setAttribute("aria-haspopup", "dialog");
      button.addEventListener("click", () => this.#openFilters(filter, button));
      this.#filterButtons.set(filter, button);
      this.#filterToolbar!.append(button);
    });
    this.#resultsHeader = document.createElement("div");
    this.#resultsHeader.className = "ab-products__results-header";
    this.#resultsHeader.hidden = true;
    this.#summary = document.createElement("p");
    this.#summary.className = "ab-products__filter-summary";
    this.#summary.setAttribute("role", "status");
    this.#reset = document.createElement("button");
    this.#reset.type = "button";
    this.#reset.className = "ab-products__filter-reset";
    this.#reset.textContent = "Azzera filtri";
    this.#reset.addEventListener("click", () => {
      (this.#getFilter("ab-filter-lens-compatibility") as AbFilterLensCompatibilityElement | undefined)?.resetSelection();
      this.#controller?.resetFilters();
    });
    this.#empty = document.createElement("p");
    this.#empty.className = "ab-products__empty";
    this.#empty.textContent = "Nessun prodotto corrisponde ai filtri applicati.";
    this.#filterPanel.append(...this.#filterElements!);
    this.#resultsHeader.append(this.#summary, this.#reset);
    this.#results.append(this.#filterToolbar, this.#resultsHeader, this.#empty);
    this.append(this.#filterPanel, this.#results);
    this.#layoutObserver = new ResizeObserver(([entry]) => {
      if (entry) this.#syncLayout(entry.contentRect.width);
    });
    this.#layoutObserver.observe(this);
    this.#syncLayout(this.clientWidth);
  }

  #syncLayout(width: number) {
    const sidebar = this.#filtersReady && width >= SIDEBAR_MIN_WIDTH;
    this.classList.toggle("ab-products--sidebar", sidebar);
    if (sidebar) this.#modal?.hide();
  }

  #openFilters(filter?: HTMLElement, opener = this.#filterTrigger) {
    if (!this.#filterPanel || this.#modal || this.classList.contains("ab-products--sidebar")) return;
    const tag = filter?.tagName.toLowerCase() as ProductFilterTag | undefined;
    const title = tag ? FILTER_CHIP_LABELS[tag] : "Filtri";
    let content: HTMLElement = this.#filterPanel;
    let placeholder: Comment | undefined;
    let details: HTMLDetailsElement | null = null;
    let wasOpen = false;
    if (filter) {
      placeholder = document.createComment("filter position");
      filter.replaceWith(placeholder);
      details = filter.querySelector("details");
      wasOpen = details?.open ?? false;
      if (details) details.open = true;
      content = document.createElement("div");
      content.className = "ab-products__filters ab-products__filters--single";
      content.append(filter);
    }

    const dialogContent = document.createElement("div");
    dialogContent.className = "ab-products__filter-dialog";
    const scrollArea = document.createElement("div");
    scrollArea.className = "ab-products__filter-dialog-scroll";
    scrollArea.append(content);
    const footer = document.createElement("div");
    footer.className = "ab-products__filter-dialog-footer";
    const showProducts = document.createElement("button");
    showProducts.type = "button";
    showProducts.className = "ab-products__filter-apply ab-btn";
    showProducts.textContent = "Mostra prodotti";
    showProducts.addEventListener("click", () => this.#modal?.hide());
    footer.append(showProducts);
    dialogContent.append(scrollArea, footer);

    this.#modal = new AbModal({
      autoOpen: false, handleBackBrowser: false, hideBodyOverflow: true, id: "product-filters",
      attributes: {
        classesModal: "ab-modal--product-filters",
        classesBackdrop: "ab-modal__backdrop--dark-50",
        classesInner: "", classesWrapper: "",
        header: { show: true, text: title, closeButton: true, closeButtonText: "Chiudi" },
      },
      handlers: { onClose: () => {
        if (filter && placeholder) {
          if (details) details.open = wasOpen;
          placeholder.replaceWith(filter);
        } else if (this.#filterPanel) {
          this.insertBefore(this.#filterPanel, this.#results ?? null);
        }
        this.#modal = undefined;
        if (this.isConnected) {
          if (this.classList.contains("ab-products--sidebar")) this.#filterPanel?.querySelector<HTMLElement>("summary")?.focus();
          else opener?.focus();
        }
      } },
    }, dialogContent);
    this.#modal.show();
    document.getElementById(this.#modal.id)?.querySelector<HTMLButtonElement>(".ab-modal__close")?.focus();
  }

  #renderState(state: ProductListingState) {
    if (!this.#renderer) return;
    if (state.status === "idle") return;
    if (state.status === "loading" || state.status === "error") {
      this.#modal?.hide();
      this.#filtersReady = false;
      this.#syncLayout(this.clientWidth);
      if (this.#filterPanel) this.#filterPanel.hidden = true;
      if (this.#filterToolbar) this.#filterToolbar.hidden = true;
      if (this.#resultsHeader) this.#resultsHeader.hidden = true;
      this.#filterElements?.forEach((element) => { element.hidden = true; });
      if (this.#empty) this.#empty.hidden = true;
      if (state.status === "loading") this.#renderer.setLoading();
      else this.#renderer.setError();
      return;
    }
    if (this.#filterPanel) {
      this.#filtersReady = true;
      this.#syncLayout(this.clientWidth);
      this.#filterPanel.hidden = false;
      this.#filterToolbar!.hidden = false;
      this.#resultsHeader!.hidden = false;
      this.#filterElements?.forEach((element) => { element.hidden = false; });
      const active = Object.values(state.selections).some((values) => values.length);
      const total = state.composedItems.length;
      const productLabel = getProductListingCountLabel(state.composedItems);
      this.#summary!.textContent = active
        ? String(state.visibleItems.length) + " di " + String(total) + " " + productLabel
        : String(total) + " " + productLabel;
      this.#filterTrigger!.dataset.active = String(active);
      this.#filterButtons.forEach((button, filter) => {
        const tag = filter.tagName.toLowerCase() as ProductFilterTag;
        const selected = state.selections[FILTER_SELECTION_KEYS[tag]].length > 0;
        button.dataset.active = String(selected);
      });
      this.#reset!.hidden = !active;
      this.#empty!.hidden = state.visibleItems.length > 0 || !active;
      (this.#getFilter("ab-filter-brand") as AbFilterBrandElement | undefined)
        ?.update(getBrandOptions(state.composedItems), state.selections.brands);
      (this.#getFilter("ab-filter-product-type") as AbFilterProductTypeElement | undefined)
        ?.update(getProductTypeOptions(state.composedItems), state.selections.productTypes);
      (this.#getFilter("ab-filter-sensor-format") as AbFilterSensorFormatElement | undefined)
        ?.update(getSensorFormatOptions(state.composedItems), state.selections.sensorFormats);
      (this.#getFilter("ab-filter-lens-compatibility") as AbFilterLensCompatibilityElement | undefined)
        ?.update(getLensMountOptions(state.composedItems));
      (this.#getFilter("ab-filter-target") as AbFilterTargetElement | undefined)
        ?.update(getTargetOptions(state.composedItems), state.selections.targets);
      (this.#getFilter("ab-filter-sub-category") as AbFilterSubCategoryElement | undefined)
        ?.update(getSubCategoryOptions(state.composedItems), state.selections.subCategories);
      (this.#getFilter("ab-filter-usage") as AbFilterUsageElement | undefined)
        ?.update(getUsageOptions(state.composedItems), state.selections.usages);
    }
    this.#renderer.setModels(createProductCardViewModels(state.visibleItems).map((model) => ({ ...model,
        ...(this.#sectionLinks[model.product._id] ? { sectionLink: this.#sectionLinks[model.product._id] } : {}),
      })));
  }

  #destroyRenderer() {
    this.#renderer?.destroy();
    this.#renderer = undefined;
  }

  #showConfigurationError(error: unknown) {
    const message = error instanceof ProductListingConfigurationError ? error.message : "Invalid product listing configuration.";
    this.#destroyRenderer();
    this.#filtersReady = false;
    this.classList.remove("ab-products--sidebar");
    this.#modal?.hide();
    if (this.#filterPanel) this.append(...this.#filterElements!);
    this.#filterPanel?.remove();
    this.#filterPanel = undefined;
    this.#results?.remove();
    this.#results = undefined;
    this.#resultsHeader = undefined;
    this.#filterToolbar = undefined;
    this.#filterButtons.clear();
    this.#layoutObserver?.disconnect();
    this.#empty?.remove();
    this.#filterElements?.forEach((element) => { element.hidden = true; });
    this.#configurationError?.remove();
    this.#configurationError = Object.assign(document.createElement("div"), {
      className: "ab-products ab-products--error", textContent: message,
    });
    this.appendChild(this.#configurationError);
  }
}

export function defineAbProductsElement() {
  defineAbFilterBrandElement();
  defineAbFilterProductTypeElement();
  defineAbFilterSensorFormatElement();
  defineAbFilterLensCompatibilityElement();
  defineAbFilterTargetElement();
  defineAbFilterSubCategoryElement();
  defineAbFilterUsageElement();
  if (!customElements.get(TAG_NAME)) customElements.define(TAG_NAME, AbProductsElement);
}
